// file: src/pages/LandlordSignIn.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { landlordLogin } from "../api/landlordApi";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
/* ---- Theme ---- */
const BLUE = "#2A7EFA";
const BLUE_LIGHT = "#5AA9FF";

/* ---- IMPORTANT: set this to a real endpoint that returns 200 when logged in ----
   If you don’t have /landlord/ping yet, temporarily point to your working cheap endpoint,
   e.g.: "https://towing-portal-api.onrender.com/api/v1/do-not-tow/?limit=1"
*/
const LANDLORD_AUTH_PROBE_URL =
  "https://towing-portal-api.onrender.com/api/v1/do-not-tow/?limit=1";

const PROBE_TTL_MS = 30_000;
const PROBE_CACHE_KEY = "landlord_probe_cache_v1";

/* ---- Remember store ---- */
const rememberStore = {
  get() {
    try {
      return JSON.parse(localStorage.getItem("landlordRemember") || "{}");
    } catch {
      return {};
    }
  },
  set(email) {
    try {
      localStorage.setItem("landlordRemember", JSON.stringify({ email }));
    } catch {}
  },
  clear() {
    try {
      localStorage.removeItem("landlordRemember");
    } catch {}
  },
  has() {
    try {
      const raw = localStorage.getItem("landlordRemember");
      if (!raw) return false;
      const obj = JSON.parse(raw);
      return !!obj?.email;
    } catch {
      return false;
    }
  },
};

/* ---- Probe cache ---- */
function getProbeCache() {
  try {
    const raw = sessionStorage.getItem(PROBE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}
function setProbeCache(ok) {
  try {
    sessionStorage.setItem(
      PROBE_CACHE_KEY,
      JSON.stringify({ ok: !!ok, ts: Date.now() })
    );
  } catch {}
}
function clearProbeCache() {
  try {
    sessionStorage.removeItem(PROBE_CACHE_KEY);
  } catch {}
}

/* ---- Logo ---- */
const Logo = ({ size = 40 }) => (
  <div
    className="grid place-items-center rounded-2xl text-white shadow"
    style={{
      width: size,
      height: size,
      background: `linear-gradient(135deg, ${BLUE}, ${BLUE_LIGHT})`,
      boxShadow: `0 12px 28px ${BLUE}33`,
    }}
    aria-label="TowPro"
  >
    <svg
      viewBox="0 0 24 24"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path d="M4 13l1.4-4.2A2 2 0 0 1 7.3 7h9.4a2 2 0 0 1 1.9 1.8L20 13" />
      <path
        d="M3 13h18v4a1 1 0 0 1-1 1h-1.5a1 1 0 0 1-1-1v-1H6.5v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4z"
        fill="currentColor"
      />
    </svg>
  </div>
);

/* ---- Icons ---- */
const Eye = (p) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    {...p}
  >
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3.5" />
  </svg>
);
const EyeOff = (p) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    {...p}
  >
    <path d="M3 3l18 18M2 12s3.5-7 10-7c1.7 0 3.2.4 4.6 1.1M22 12s-3.5 7-10 7c-1.7 0-3.2-.4-4.6-1.1" />
  </svg>
);

/* ---- Error banner ---- */
function ErrorBanner({ message }) {
  if (!message) return null;
  return (
    <div
      className="mb-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900 shadow"
      role="alert"
      aria-live="assertive"
    >
      {message}
    </div>
  );
}

/* ---- Validation ---- */
const SignInSchema = Yup.object({
  email: Yup.string()
    .email("Enter a valid email")
    .required("Email is required"),
  password: Yup.string()
    .min(8, "Min 8 characters")
    .required("Password is required"),
  remember: Yup.boolean(),
});

/* ---- Helpers ---- */
function classifyError(e) {
  const status = e?.response?.status;
  const msg = (e?.response?.data?.message || e?.message || "").toLowerCase();
  const isAuthError =
    status === 400 ||
    status === 401 ||
    msg.includes("invalid") ||
    msg.includes("unauthorized") ||
    msg.includes("bad credentials") ||
    msg.includes("auth/invalid-credential");
  return isAuthError
    ? "Invalid email or password"
    : "Sign in failed. Please try again.";
}

function clearAllStorageButRemember(emailToKeep) {
  try {
    sessionStorage.clear();
  } catch {}
  // Keep landlordRemember if we intend to remember; otherwise clear it.
  if (emailToKeep) rememberStore.set(emailToKeep);
  else rememberStore.clear();
}

/** Cheap cookie probe (always resolves boolean) */
async function probeSession(timeoutMs = 2500) {
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), timeoutMs);
  try {
    const res = await fetch(LANDLORD_AUTH_PROBE_URL, {
      method: "GET",
      credentials: "include",
      cache: "no-store",
      signal: ctl.signal,
      headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
    });
    console.debug("[LandlordSignIn] probe", res.status);
    return res.ok;
  } catch (err) {
    console.debug("[LandlordSignIn] probe error", err?.name || err);
    return false;
  } finally {
    clearTimeout(t);
  }
}

/* ---- Password input (Formik-friendly) ---- */
function PasswordInput({ id, name, placeholder }) {
  const [show, setShow] = React.useState(false);
  return (
    <div className="relative">
      <Field
        id={id}
        name={name}
        type={show ? "text" : "password"}
        autoComplete="current-password"
        placeholder={placeholder}
        className="mt-1 w-full rounded-xl border border-black/[0.06] bg-white px-4 py-3 pr-11 outline-none focus:ring-4"
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute inset-y-0 right-2 grid place-items-center rounded-lg px-2 text-slate-600 hover:text-slate-900"
        aria-label={show ? "Hide password" : "Show password"}
      >
        {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
      </button>
    </div>
  );
}

/* ---- Component ---- */
export default function LandlordSignIn({
  onSignIn,
  onSuccess,
  redirectTo = "/landlord/dashboard",
}) {
  const nav = useNavigate();
  const { search } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);

  // If redirected from logout → skip probe once & clear cache
  const skipProbeOnce = params.get("logout") === "1";
  if (skipProbeOnce) clearProbeCache();

  const remembered = rememberStore.get();
  const allowAutoLogin = !!remembered.email && !skipProbeOnce;

  const [submitError, setSubmitError] = useState("");
  const [probing, setProbing] = useState(allowAutoLogin);
  const redirectedOnceRef = useRef(false);

  useEffect(() => {
    document.title = "Landlord Sign In";
  }, []);

  useEffect(() => {
    // If we shouldn't auto-login, just stop probing & show the form.
    if (!allowAutoLogin) {
      setProbing(false);
      return;
    }

    let alive = true;
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!alive) return;

      if (user && !redirectedOnceRef.current) {
        try {
          await user.getIdToken(false);
        } catch {}
        redirectedOnceRef.current = true;
        nav(redirectTo, { replace: true });
        setProbing(false);
        return;
      }

      // No user -> show the form
      setProbing(false);
    });

    return () => {
      alive = false;
      unsub();
    };
  }, [allowAutoLogin, nav, redirectTo]);

  return (
    <div className="relative min-h-screen bg-white text-slate-900">
      {/* Soft background accents */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div
          className="absolute -top-24 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full blur-3xl"
          style={{ background: `${BLUE}14` }}
        />
        <div
          className="absolute -bottom-24 right-8 h-72 w-72 rounded-full blur-3xl"
          style={{ background: `${BLUE_LIGHT}12` }}
        />
      </div>

      <div className="mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 py-8">
        <div className="w-full max-w-md rounded-3xl border border-black/[0.06] bg-white/90 p-6 shadow-xl backdrop-blur">
          {/* Brand */}
          <div className="flex items-center gap-3">
            <Logo size={36} />
            <div className="text-base font-semibold tracking-tight">
              Towing Landlord Portal
            </div>
          </div>

          <h1 className="mt-5 text-2xl font-bold tracking-tight">
            Welcome back
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Sign in to access your valid reservations.
          </p>

          {/* Tiny banner while probing (non-blocking) */}
          {probing && (
            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              Checking your session…
            </div>
          )}

          {/* Form-level error */}
          <ErrorBanner message={submitError} />

          <Formik
            initialValues={{
              email: remembered.email || "",
              password: "",
              remember: Boolean(remembered.email),
            }}
            validationSchema={SignInSchema}
            onSubmit={async (values, { setSubmitting }) => {
              setSubmitError("");
              setSubmitting(true);

              // snapshot remember email intention
              const memoEmail = values.remember ? values.email : "";
              clearAllStorageButRemember(memoEmail);

              try {
                const payload = await landlordLogin({
                  email: values.email,
                  password: values.password,
                  remember: values.remember,
                });

                // Warm probe (don’t block)
                probeSession(1200)
                  .then((ok) => setProbeCache(ok))
                  .catch(() => {});

                // Callbacks (non-blocking)
                try {
                  if (typeof onSuccess === "function") onSuccess(payload);
                  if (typeof onSignIn === "function") onSignIn(payload);
                } catch {}

                // ALWAYS navigate after successful login
                nav(redirectTo, { replace: true });
              } catch (e) {
                setSubmitError(classifyError(e));
                clearProbeCache();
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ isSubmitting, values, setFieldValue }) => (
              <Form className="mt-6 space-y-4">
                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium">
                    Email
                  </label>
                  <Field
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@company.com"
                    className="mt-1 w-full rounded-xl border border-black/[0.06] bg-white px-4 py-3 outline-none focus:ring-4"
                    disabled={isSubmitting || probing}
                  />
                  <ErrorMessage
                    name="email"
                    component="div"
                    className="mt-1 text-xs text-rose-600"
                  />
                </div>

                {/* Password */}
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium"
                  >
                    Password
                  </label>
                  <PasswordInput
                    id="password"
                    name="password"
                    placeholder="••••••••"
                  />
                  <ErrorMessage
                    name="password"
                    component="div"
                    className="mt-1 text-xs text-rose-600"
                  />
                </div>

                {/* Remember / Forgot */}
                <div className="flex items-center justify-between">
                  <label className="inline-flex select-none items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-[color:var(--blue)]"
                      style={{ ["--blue"]: BLUE }}
                      checked={values.remember}
                      onChange={(e) =>
                        setFieldValue("remember", e.target.checked)
                      }
                      disabled={isSubmitting || probing}
                    />
                    Remember me
                  </label>

                  <Link
                    to="/landlord/password-reset"
                    className="text-sm text-[color:var(--blue)] hover:underline"
                    style={{ ["--blue"]: BLUE }}
                  >
                    Forgot password?
                  </Link>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting || probing}
                  className="group relative inline-flex w-full items-center justify-center overflow-hidden rounded-xl bg-[color:var(--blue)] px-4 py-3 font-medium text-white shadow-lg transition active:scale-[0.99] disabled:opacity-60"
                  style={{
                    ["--blue"]: BLUE,
                    boxShadow: `0 12px 26px ${BLUE}33`,
                  }}
                >
                  <span className="absolute inset-0 -translate-x-full bg-white/15 transition-transform duration-700 group-hover:translate-x-full" />
                  {isSubmitting ? "Signing in…" : "Sign in"}
                </button>

                {/* Divider */}
                <div className="relative my-2 text-center">
                  <div className="absolute inset-x-0 top-1/2 -z-10 h-px -translate-y-1/2 bg-black/10" />
                </div>

                {/* Footer text */}
                <p className="text-center text-xs text-slate-500">
                  Protected by best-practice security. Need access? Contact your
                  administrator.
                </p>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
}
